import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getDeployment } from '@/redux/modules/config';
import { AppDispatch, AppGetState } from '@/redux/store';

import { ArticleSummary } from '../article/article.types';
import { ConfigSlice } from '../config';
import { searchArticleLinks } from './articleLinks.api';
import { startArticleLinksNavigation } from './articleLinks.reducer';
import { getArticleLinksNavigationId } from './articleLinks.selectors';
import { ArticleLinksRequestParams, FetchArticleLinksParams, SearchArticleLinksParams } from './articleLinks.types';

export const fetchArticleLinks = createAsyncThunk<ArticleSummary[], FetchArticleLinksParams>(
    'la/ui/articleLinks/fetchArticleLinks',
    async (params, { getState }) => {
        const requestParams = toArticleLinksRequestParams({
            ...params,
            deployment: getDeployment(getState()),
        });

        if (!requestParams) {
            return [];
        }

        return searchArticleLinks(requestParams);
    }
);

/**
 * Bumps article-links navigation and returns the id for this loader pass.
 * Call before any await so a newer navigation can supersede in-flight fetches.
 */
export const beginArticleLinksNavigation =
    () =>
    (dispatch: AppDispatch, getState: AppGetState): number => {
        dispatch(startArticleLinksNavigation());
        return getArticleLinksNavigationId(getState());
    };

/**
 * Trims the brand/category/keyword discriminant and returns API params, or null when blank.
 */
const toArticleLinksRequestParams = ({
    brand,
    category,
    keyword,
    deployment,
    searchLimit,
}: SearchArticleLinksParams & { deployment: ConfigSlice['deployment'] }): ArticleLinksRequestParams | null => {
    if (brand !== undefined) {
        const trimmed = brand.trim();
        return trimmed ? { brand: trimmed, deployment, searchLimit } : null;
    }

    if (category !== undefined) {
        const trimmed = category.trim();
        return trimmed ? { category: trimmed, deployment, searchLimit } : null;
    }

    const trimmed = keyword.trim();
    return trimmed ? { deployment, keyword: trimmed, searchLimit } : null;
};
